/**
 * TemplateVersionHistory Component
 *
 * Displays version history for a CLAUDE.md template with visual distinction
 * between user-created and agent-created versions.
 *
 * @module components/claude-templates/TemplateVersionHistory
 */

import { useState, useEffect, useCallback, memo } from 'react';
import { Clock, User, Bot, Loader2, RotateCcw, ChevronDown, ChevronRight, MessageSquare, Eye } from 'lucide-react';

const API_BASE = '/api';

/**
 * Fetch version history for a template
 */
async function fetchVersions(templateId) {
  const res = await fetch(`${API_BASE}/claude-templates/${templateId}/versions`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load versions');
  return res.json();
}

/**
 * Fetch a specific version with full content
 */
async function fetchVersionContent(templateId, versionId) {
  const res = await fetch(`${API_BASE}/claude-templates/${templateId}/versions/${versionId}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to load version content');
  return res.json();
}

/**
 * Rollback to a specific version
 */
async function rollbackToVersion(templateId, versionId) {
  const res = await fetch(`${API_BASE}/claude-templates/${templateId}/rollback/${versionId}`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.message || 'Failed to rollback');
  }
  return res.json();
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const VersionItem = memo(function VersionItem({ version, index, total, onRollback, onPreview, isRollingBack, isPreviewLoading, isSelected }) {
  const isAgent = version.createdByType === 'agent';
  const isLatest = index === 0;

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer hover:bg-gray-800/60 ${
        isSelected
          ? 'bg-indigo-600/20 border-indigo-500/50 ring-1 ring-indigo-500/30'
          : isAgent
            ? 'bg-purple-900/10 border-purple-500/20'
            : 'bg-gray-800/30 border-gray-700/50'
      }`}
      onClick={() => onPreview?.(version.id)}
      title="Click to view this version"
    >
      {/* Author Icon */}
      <div className={`mt-0.5 p-1.5 rounded-full flex-shrink-0 ${
        isAgent ? 'bg-purple-500/20' : 'bg-gray-700'
      }`}>
        {isAgent ? (
          <Bot className="w-3.5 h-3.5 text-purple-400" />
        ) : (
          <User className="w-3.5 h-3.5 text-gray-400" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-200">
            v{total - index}
          </span>
          {isAgent && (
            <span className="px-1.5 py-0.5 text-xs font-medium text-purple-300 bg-purple-500/20 border border-purple-500/30 rounded-full">
              Agent Edit
            </span>
          )}
          {isLatest && (
            <span className="px-1.5 py-0.5 text-xs font-medium text-green-300 bg-green-500/20 border border-green-500/30 rounded-full">
              Current
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          <span>{formatDate(version.createdAt)}</span>
          {version.createdByName && (
            <>
              <span className="text-gray-600">—</span>
              <span className={isAgent ? 'text-purple-400' : 'text-gray-400'}>
                {version.createdByName}
              </span>
            </>
          )}
        </div>

        {/* Edit Summary for agent edits */}
        {version.editSummary && (
          <div className="flex items-start gap-1.5 mt-1.5">
            <MessageSquare className="w-3 h-3 text-purple-400 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-purple-300/80">{version.editSummary}</p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {!isLatest && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRollback(version.id); }}
            disabled={isRollingBack}
            className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
            title="Rollback to this version"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
});

/**
 * TemplateVersionHistory
 *
 * @param {object} props
 * @param {string} props.templateId - Template ID to show history for
 * @param {function} props.onRollback - Callback after successful rollback (receives restored data)
 * @param {function} [props.onPreview] - Callback when a version is clicked for preview (receives version content)
 * @param {string} [props.className] - Additional CSS classes
 */
const TemplateVersionHistory = memo(function TemplateVersionHistory({
  templateId,
  onRollback,
  onPreview,
  className = '',
}) {
  const [versions, setVersions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [error, setError] = useState(null);

  const loadVersions = useCallback(async () => {
    if (!templateId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchVersions(templateId);
      setVersions(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    if (isExpanded && templateId) {
      loadVersions();
    }
  }, [isExpanded, templateId, loadVersions]);

  const handleRollback = useCallback(async (versionId) => {
    if (!templateId) return;
    setIsRollingBack(true);
    try {
      const result = await rollbackToVersion(templateId, versionId);
      setSelectedVersionId(null);
      onRollback?.(result.data || result);
      loadVersions();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsRollingBack(false);
    }
  }, [templateId, onRollback, loadVersions]);

  const handlePreview = useCallback(async (versionId) => {
    if (!templateId || !onPreview) return;
    setSelectedVersionId(versionId);
    setIsPreviewLoading(true);
    try {
      const result = await fetchVersionContent(templateId, versionId);
      onPreview(result.data || result);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [templateId, onPreview]);

  const agentEditCount = versions.filter(v => v.createdByType === 'agent').length;

  return (
    <div className={`border-t border-gray-700 ${className}`}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-400 hover:text-gray-200 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <Clock className="w-4 h-4" />
          <span>Version History</span>
          {versions.length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-gray-700 text-gray-400 rounded-full">
              {versions.length}
            </span>
          )}
          {agentEditCount > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-full">
              {agentEditCount} agent
            </span>
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-2 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
            </div>
          ) : error ? (
            <div className="text-sm text-red-400 py-2">{error}</div>
          ) : versions.length === 0 ? (
            <div className="text-sm text-gray-500 py-2">No versions yet</div>
          ) : (
            versions.map((version, idx) => (
              <VersionItem
                key={version.id}
                version={version}
                index={idx}
                total={versions.length}
                onRollback={handleRollback}
                onPreview={handlePreview}
                isRollingBack={isRollingBack}
                isPreviewLoading={isPreviewLoading}
                isSelected={selectedVersionId === version.id}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
});

export default TemplateVersionHistory;
